import { Megaphone, MessagesSquare, Settings2, ArrowDown } from "lucide-react";
import VisualDemo, { OutcomeGraphics } from "@/components/marketing/VisualDemo";
import LeadForm from "@/components/marketing/LeadForm";
import { SEO } from "@/components/SEO";
import {
  Action,
  CaseStudies,
  FreePlanCTA,
} from "@/components/marketing/Marketing";
export default function HomePage() {
  return (
    <div className="growth">
      <SEO
        title="Put AI to work in your business"
        description="Practical AI, automation, and custom apps that connect your leads, team, and daily work. Start with a free AI Opportunity Plan."
      />
      <section className="hero hero-graphic">
        <div className="wrap split">
          <div>
            <span className="eyebrow">
              Practical AI. Built around your business.
            </span>
            <h1>
              More customers.
              <br />
              Less busywork.
              <br />
              <em>Powered by AI.</em>
            </h1>
            <p className="intro">
              We build and manage AI systems that help you get leads, follow up,
              answer emails, and automate everyday work.
            </p>
            <div className="actions">
              <Action href="/contact">Tell us what you need</Action>
              <Action href="/ai-sales" secondary>
                Get my free AI plan
              </Action>
            </div>
            <p className="small">
              AI & automation services · $500–$1,500/month to start
            </p>
          </div>
          <VisualDemo />
        </div>
      </section>
      <div className="wrap">
        <div className="logo-proof">
          <span>
            BUILT FOR BUSINESSES
            <br />
            LIKE THESE
          </span>
          {[
            ["desertmoon", "Desert Moon Lighting"],
            ["ssw", "Soil Seed & Water"],
            ["testal", "Testal Mexican Kitchen"],
            ["compostdeveloper", "CompostDeveloper"],
            ["agave", "Agave Fleet"],
          ].map(([file, name]) => (
            <img
              key={file}
              src={`/portfolio/img/logos/${file}.png`}
              alt={name}
            />
          ))}
        </div>
      </div>
      <section className="section" id="solutions">
        <div className="wrap">
          <div className="sectionhead">
            <div>
              <span className="eyebrow">What gets easier</span>
              <h2>
                Three ways we put
                <br />
                AI to work for you.
              </h2>
            </div>
            <p>
              Choose where you need help. We build the system and help keep it
              running.
            </p>
          </div>
          <OutcomeGraphics />
        </div>
      </section>
      <section className="section scale-section" id="what-to-build">
        <div className="wrap scale-layout">
          <div className="scale-message">
            <span className="eyebrow">Your next stage of growth</span>
            <h2>Grow your business.<br /><em>Let AI handle more of the work.</em></h2>
            <p className="intro">Bring in more customers, follow up faster, and keep daily work moving.</p>
            <p>You don’t need to know what to build. Tell us what’s slowing you down. We’ll help you choose what to automate first—then build it for you.</p>
            <Action href="/contact">Find my starting point</Action>
          </div>
          <div className="business-map" aria-label="Connected marketing, sales, and operations">
            <div className="business-map-heading"><span>Your business, connected</span><span>THREE PLACES TO START</span></div>
            {[
              { icon: Megaphone, name: "Marketing", outcome: "Bring the right people in.", detail: "Content, ads, and lead capture" },
              { icon: MessagesSquare, name: "Sales", outcome: "Turn interest into customers.", detail: "AI conversations, quotes, and follow-up" },
              { icon: Settings2, name: "Operations", outcome: "Deliver without the busywork.", detail: "Scheduling, invoicing, and team handoffs" },
            ].map(({ icon: Icon, name, outcome, detail }, i) => (
              <div className="business-map-stage" key={name}>
                <span className="business-map-icon"><Icon size={21} /></span>
                <div><span className="business-map-label">{name}</span><h3>{outcome}</h3><p>{detail}</p></div>
                {i < 2 && <ArrowDown className="business-map-arrow" size={14} />}
              </div>
            ))}
            <div className="business-map-note">One useful system first. More as your business grows.</div>
          </div>
        </div>
      </section>
      <section className="section" style={{ background: "#eef3eb" }} id="work">
        <div className="wrap">
          <div className="sectionhead">
            <div>
              <span className="eyebrow">Real businesses. Real workflows.</span>
              <h2>
                Built for the way
                <br />
                work actually happens.
              </h2>
            </div>
            <p>
              From a lighting quote to a compost yard’s field records. The tools
              change. The goal stays simple: make work easier.
            </p>
          </div>
          <CaseStudies />
        </div>
      </section>
      <section className="section" id="process">
        <div className="wrap">
          <span className="eyebrow">How we work together</span>
          <h2>Start with one useful improvement.</h2>
          <div className="process">
            {[
              [
                "01",
                "Find the bottleneck",
                "We walk through your actual process, current tools, and the work your team repeats.",
              ],
              [
                "02",
                "Build what fits",
                "Agree on scope, cost, and success before building. Keep human approval where it matters.",
              ],
              [
                "03",
                "Launch with your team",
                "Test the workflow, train the people using it, and agree on support as the system grows.",
              ],
            ].map(([n, t, b]) => (
              <div key={n}>
                <span className="eyebrow">{n}</span>
                <h3>{t}</h3>
                <p className="muted">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="section" id="get-started">
        <div className="wrap split" style={{ alignItems: "start" }}>
          <div>
            <span className="eyebrow">Start here</span>
            <h2>
              What do you want
              <br />
              AI to take off your plate?
            </h2>
            <p className="intro">
              Tell us what your business does and where you need help. We’ll
              find a focused place to start.
            </p>
            <div className="offer-chip">
              Focused services · $500–$1,500/month
            </div>
            <p className="small" style={{ marginTop: 16 }}>
              Setup, custom builds, tools, and ad spend quoted separately.
            </p>
          </div>
          <div className="formpanel">
            <LeadForm />
          </div>
        </div>
      </section>
      <FreePlanCTA />
      <section className="section">
        <div className="wrap" style={{ maxWidth: 840 }}>
          <span className="eyebrow">Good questions</span>
          <h2>You don’t need to have it all figured out.</h2>
          {[
            [
              "Do I need to know what AI can do?",
              "No. Tell us where work gets stuck. We’ll help separate useful automation from things that still need a person.",
            ],
            [
              "Will we have to replace all our software?",
              "Not necessarily. We first look at what you use and what can connect. Custom software fills gaps when your existing tools cannot.",
            ],
            [
              "How much does it cost?",
              "The Opportunity Plan and introductory call are free. Focused ongoing services start in the $500–$1,500/month range. Setup, custom development, software, and ad spend are quoted separately. You get a written scope before work begins.",
            ],
            [
              "Can you build an app for our team?",
              "Yes. We build web and native mobile workflows for sales, field work, and operations. We scope device needs, access, integrations, and rollout with your team.",
            ],
          ].map(([q, a]) => (
            <details key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
